/**
 * editor2/modals/RecalcModal.tsx
 * 合計金額再計算モーダル
 * base_price + option_price + noc_price + cdw_price - coupon - point = total
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import type { EditorReservation } from '../types';

interface RecalcModalProps {
  open: boolean;
  data: EditorReservation;
  onApply: (sum: number) => void;
  onCancel: () => void;
}

export default function RecalcModal({ open, data, onApply, onCancel }: RecalcModalProps) {
  const items = [
    { label: '基本料金', value: Number(data.base_price) || 0 },
    { label: 'オプション料金', value: Number(data.option_price) || 0 },
    {
      label: 'NOC料金',
      value: data.noc ? Number(data.noc_price) || 0 : 0,
      note: !data.noc ? '(OFF)' : '',
    },
    {
      label: 'CDW料金',
      value: data.cdw ? Number(data.cdw_price) || 0 : 0,
      note: !data.cdw ? '(OFF)' : '',
    },
  ];

  const subtotal = items.reduce((s, i) => s + i.value, 0);
  const couponVal = Number(String(data.coupon).replace(/[^0-9]/g, '')) || 0;
  const pointVal = Number(data.point) || 0;
  const calculated = subtotal - couponVal - pointVal;
  const currentSum = Number(data.sum_price) || 0;
  const diff = calculated - currentSum;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>合計金額 再計算</DialogTitle>
      <DialogContent dividers>
        <Box>
          {/* 明細 */}
          {items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.8,
                px: 1,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {item.label}{' '}
                {item.note && (
                  <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{item.note}</span>
                )}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ¥{item.value.toLocaleString()}
              </Typography>
            </Box>
          ))}

          {/* 小計 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              py: 0.8,
              px: 1,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              小計
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ¥{subtotal.toLocaleString()}
            </Typography>
          </Box>

          {/* 割引 */}
          {couponVal > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.8,
                px: 1,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Typography variant="body2" color="error.main">
                クーポン
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                -¥{couponVal.toLocaleString()}
              </Typography>
            </Box>
          )}
          {pointVal > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.8,
                px: 1,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Typography variant="body2" color="error.main">
                ポイント
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                -¥{pointVal.toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* ビフォーアフター比較 */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, px: 1 }}>
            <Box
              sx={{
                flex: 1,
                bgcolor: '#fef2f2',
                borderRadius: 2,
                p: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                現在の合計金額
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#64748b' }}>
                ¥{currentSum.toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                bgcolor: '#eff6ff',
                borderRadius: 2,
                p: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                再計算結果
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                ¥{calculated.toLocaleString()}
              </Typography>
            </Box>
          </Box>

          {/* 差額 */}
          {diff !== 0 && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: diff > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}
              >
                差額: {diff > 0 ? '+' : ''}¥{diff.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} color="inherit" sx={{ textTransform: 'none' }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => onApply(calculated)}
          sx={{ textTransform: 'none', minWidth: 80 }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
